import Cropper from "react-easy-crop";
import { useCallback, useState, useEffect } from "react";
import {
  Box,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { getCroppedBlob, compressImage } from "@/utils/image";

export default function Crop({
  file,
  setFile,
  setEditing,
  onDone,
  cropShape = "rect",
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [compressedFile, setCompressedFile] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    if (!file) {
      setCompressedFile(null);
      return;
    }
    let isMounted = true;
    const compress = async () => {
      const cf = await compressImage(file);
      if (isMounted) {
        setCompressedFile(cf);
      }
    };
    compress();
    return () => {
      isMounted = false;
    };
  }, [file]);

  useEffect(() => {
    if (compressedFile) {
      const newUrl = URL.createObjectURL(compressedFile);
      setObjectUrl(newUrl);
      return () => {
        URL.revokeObjectURL(newUrl);
        setObjectUrl(null); // Clear objectUrl state as well on cleanup
      };
    } else {
      // If compressedFile is nullified, ensure objectUrl is also null
      // and any existing URL (if somehow not cleaned up by return) is handled.
      // The primary revoke should happen in the cleanup of the effect that created it.
      if (objectUrl) {
        // This is a safeguard
        URL.revokeObjectURL(objectUrl);
      }
      setObjectUrl(null);
    }
  }, [compressedFile]);

  const handleCropComplete = useCallback((_, cropped) => {
    console.log(cropped);
    setArea(cropped);
  }, []);

  const handleDone = async () => {
    if (area) {
      const blob = await getCroppedBlob(compressedFile, area);
      // console.log("blob", blob);
      // show URL.createObjectURL(blob) in a new tab
      // window.open(URL.createObjectURL(blob), "_blank");
      onDone(blob);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setArea(null);
    setCompressedFile(null);
    setEditing(false);
  };

  if (!objectUrl) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100vw",
        height: "100vh",
        zIndex: 2000,
        position: "fixed",
        top: 50,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: "#000",
      }}
    >
      <AppBar position="fixed" elevation={0} sx={{ bgcolor: "#000" }}>
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <IconButton onClick={handleCancel}>
            <Close />
          </IconButton>
          <Typography variant="body1">Set Image</Typography>
          <Button onClick={handleDone} disabled={!area} variant="outlined">
            Done
          </Button>
        </Toolbar>
      </AppBar>
      <Container maxWidth="sm">
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: 1,
            mt: 4,
            zIndex: 2000,
          }}
        >
          <Cropper
            image={objectUrl}
            crop={crop}
            zoom={zoom}
            cropShape={cropShape}
            objectFit="vertical-cover"
            aspect={1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
          />
        </Box>
      </Container>
    </Box>
  );
}
